import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getReferralCodeInfo } from '../lib/api';
import { setStoredReferralCode } from '../lib/referralStorage';
import { CheckCircle2, XCircle, Loader2, Gift, ArrowRight } from 'lucide-react';
import type { ReferralCodeInfo } from '../../../shared/types';

/**
 * Referral landing page (/ref/:code) — the link friends receive. Stores the
 * code for checkout pre-fill and sends the visitor to the product list.
 * Headline/intro copy is trilingual and admin-editable via system_settings
 * (referral_landing_*); this page renders the language matching the visitor.
 */
const ReferralLandingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [info, setInfo] = useState<ReferralCodeInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const lang = i18n.language?.startsWith('zh-CN') ? 'cn' : i18n.language?.startsWith('en') ? 'en' : 'zh';
  const landing = info?.landing;
  const headline = lang === 'cn' ? landing?.headlineCn : lang === 'en' ? landing?.headlineEn : landing?.headlineZh;
  const intro = lang === 'cn' ? landing?.introCn : lang === 'en' ? landing?.introEn : landing?.introZh;
  const pointsPerDollar = landing?.pointsPerDollar || 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await getReferralCodeInfo(code);
      if (!cancelled) {
        setInfo(res.data || { valid: false, role: null, displayName: null });
        setLoading(false);
        if (res.data?.valid) setStoredReferralCode(code);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <div className="min-h-screen bg-brand-paper">
      <Header />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-brand-cinnabar/10">
            <Gift className="w-10 h-10 text-brand-cinnabar" />
          </div>

          <h1 className="text-3xl md:text-4xl font-serif tracking-widest text-brand-ink mb-4">
            {headline || (lang === 'en' ? 'Refer Friends, Share Wellness' : lang === 'cn' ? '推荐好友・共享健康' : '推薦好友・共享健康')}
          </h1>
          <p className="text-lg text-brand-ink/70 mb-8">
            {intro ||
              (lang === 'en'
                ? 'Friends order with your referral code; every HK$1 they spend earns you 1 point.'
                : lang === 'cn'
                  ? '亲友以您的推荐码下单，每 1 港元消费即为您累积 1 积分。'
                  : '親友以您的推薦碼下單，每 1 港元消費即為您累積 1 積分。')}
          </p>

          <div className="bg-white border border-brand-ink/10 p-8 mb-8">
            {loading ? (
              <div className="flex items-center justify-center gap-2 text-brand-ink/60">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">{t('common.loading', '載入中…')}</span>
              </div>
            ) : info?.valid ? (
              <>
                <div className="flex items-center justify-center gap-2 text-green-700 mb-3">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm">
                    {info.displayName
                      ? (lang === 'en' ? `${info.displayName} invited you` : `${info.displayName} 邀請您`)
                      : t('referral.validCode', '推薦碼已套用')}
                  </span>
                </div>
                <p className="font-mono text-2xl tracking-[0.3em] text-brand-ink mb-2">{code.toUpperCase()}</p>
                <p className="text-sm text-brand-ink/50">
                  {t('referral.codeAppliedHint', '結帳時將自動填入此推薦碼')}
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center gap-2 text-brand-ink/60">
                <XCircle className="w-6 h-6 text-amber-600" />
                <p className="text-sm">{t('referral.invalidCode', '推薦碼無效或已停用，仍可正常購物。')}</p>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/products')}
              className="inline-flex items-center gap-2 px-8 py-3 bg-brand-cinnabar text-brand-paper text-lg font-medium hover:bg-brand-ink transition-colors"
            >
              {t('referral.startShopping', '瀏覽產品')}
              <ArrowRight className="w-5 h-5" />
            </button>
            <Link to="/" className="text-brand-ink/60 hover:text-brand-cinnabar underline underline-offset-4">
              {t('common.backHome', '返回首頁')}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReferralLandingPage;
